import { businessDateOf } from '../../../utils/dateUtils';
import { getOrderMarginCostState } from '../../orders/orderMarginSummary';
import { getOrderProductDisplayDimensions } from '../../orders/orderProductDisplay';

export type AnalysisMetric = 'revenue' | 'cost' | 'margin' | 'marginPercent';
export type AnalysisDimension = 'customer' | 'product';
export type MarginBucket = 'negative' | 'low' | 'medium' | 'good' | 'excellent' | 'none' | 'incomplete';

// Display-only analysis bands. The alert threshold used by notifications is
// tenant-configured separately and must not be coupled to these values.
export const MARGIN_BUCKET_THRESHOLDS = {
  low: 10,
  medium: 25,
  good: 50,
} as const;

export interface AnalysisOrder {
  id: string;
  orderNumber?: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  orderDate?: string;
  status: string;
  customer?: { id?: string; name: string };
  revenue: number;
  // `null` means an unknown cost/margin, never a real zero. Since the owner
  // decision of 2026-09-30 an incomplete plan cost (`warehouseCostPending`, e.g.
  // goods still awaiting a batch) arrives as its KNOWN part and is summed as
  // such; the flag only labels the order as partial.
  totalCost: number | null;
  warehouseCostPending?: boolean;
  margin: number | null;
  marginPercent: number | null;
  products?: AnalysisProduct[];
}

export interface AnalysisProduct {
  id: string;
  productVariantId?: string | null;
  name: string;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string> | null;
  quantity: number;
  unit?: string;
  saleQuantity: number;
  saleUnit?: string;
  unitPrice: number;
  totalPrice: number;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  catalogProductWidth?: number | null;
  catalogProductHeight?: number | null;
  catalogProductLength?: number | null;
  catalogProductThickness?: number | null;
  productCategoryDimensionDisplayProfile?: string | null;
}

export interface ProductAnalysisOrder extends AnalysisOrder {
  sourceOrderId: string;
  sourceOrderNumber: string;
  productKey: string;
  productLabel: string;
  soldQuantity: number;
  soldUnit: string;
}

export interface AnalysisAggregate {
  orderCount: number;
  /**
   * How many of those orders have an unknown cost (`totalCost === null`, e.g.
   * a WAREHOUSE_FIFO order still awaiting its batch). It is the reason a
   * `cost`/`margin`/`marginPercent` of this aggregate is `null`, and screens
   * use it to explain the missing value instead of showing a bare dash.
   * Counted per source order just like `orderCount`, so the product
   * perspective never inflates it by the number of split rows.
   */
  pendingCostOrderCount: number;
  revenue: number;
  // `null` once any contributing order has an unknown cost/margin — an
  // aggregate must never silently treat "unknown" as "zero".
  cost: number | null;
  margin: number | null;
  marginPercent: number | null;
}

export interface AnalysisGroup extends AnalysisAggregate {
  key: string;
  label: string;
  impactShare: number;
  monthly: Record<string, AnalysisAggregate>;
}

export type AnalysisGroupSortKey = 'label' | 'orderCount' | 'revenue' | 'cost' | 'marginPercent';
export type AnalysisSortDirection = 'asc' | 'desc';

export interface AnalysisGroupSort {
  key: AnalysisGroupSortKey;
  direction: AnalysisSortDirection;
}

/**
 * Groups without any order in the current slice (e.g. customers appended by
 * `appendMissingCustomerGroups`) always stay at the end of the ranking, in
 * alphabetical order, regardless of the chosen sort column or direction.
 */
export function sortAnalysisGroups(
  groups: AnalysisGroup[],
  sort: AnalysisGroupSort,
): AnalysisGroup[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const byLabel = (left: AnalysisGroup, right: AnalysisGroup) => left.label.localeCompare(right.label, 'pl');
  // Unknown cost/margin/marginPercent (pending warehouse cost) always sorts
  // last regardless of direction — it is missing information, not a real
  // low or high value.
  const compareValues = (left: AnalysisGroup, right: AnalysisGroup) => {
    if (sort.key === 'label') return byLabel(left, right) * direction;
    const leftValue = left[sort.key];
    const rightValue = right[sort.key];
    if (leftValue == null && rightValue == null) return 0;
    if (leftValue == null) return 1;
    if (rightValue == null) return -1;
    return (leftValue - rightValue) * direction;
  };
  const active = groups.filter((group) => group.orderCount > 0).sort((left, right) => (
    compareValues(left, right) || byLabel(left, right)
  ));
  const inactive = groups.filter((group) => group.orderCount === 0).sort(byLabel);
  return [...active, ...inactive];
}

export interface AnalysisCustomerRef {
  id: string;
  name: string;
}

/**
 * Appends every known customer that has no group yet as a zero row, so the
 * customer ranking lists all companies, not only those with orders in the
 * current slice. Matching follows `getAnalysisGroupIdentity`: id first, name
 * as a fallback for orders without a customer id.
 */
export function appendMissingCustomerGroups(
  groups: AnalysisGroup[],
  customers: AnalysisCustomerRef[],
): AnalysisGroup[] {
  const present = new Set(groups.map((group) => group.key));
  const missing: AnalysisGroup[] = [];
  customers.forEach((customer) => {
    const key = customer.id || customer.name;
    if (!key || present.has(key) || present.has(customer.name)) return;
    present.add(key);
    missing.push({
      key,
      label: customer.name || '—',
      ...EMPTY,
      impactShare: 0,
      monthly: {},
    });
  });
  return missing.length ? [...groups, ...missing] : groups;
}

const dimensionValue = (value: number | null | undefined) => (
  value == null || Number(value) === 0 ? '' : String(Number(value))
);

export function productAnalysisIdentity(product: AnalysisProduct): { key: string; label: string } {
  const name = product.catalogProductName || product.name || 'Produkt bez nazwy';
  // Grouping key: every raw axis (line value, else catalog), so lines that
  // differ in any dimension never merge. The label uses the shared labelled
  // format and the category profile („gr. 12 x szer. 1250 x dł. 3000 mm”).
  const dimensions = [
    product.width ?? product.catalogProductWidth,
    product.length ?? product.catalogProductLength,
    product.height ?? product.catalogProductHeight,
    product.thickness ?? product.catalogProductThickness,
  ].map(dimensionValue).filter(Boolean);
  const attributes = Object.entries(product.variantAttributes || {})
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}: ${value}`);
  const variantParts = [product.variantName || '', ...attributes].filter(Boolean);
  const keyParts = [dimensions.length > 0 ? dimensions.join(' × ') : '', ...variantParts].filter(Boolean);
  const identityParts = [name.toLocaleLowerCase('pl'), ...keyParts.map((part) => part.toLocaleLowerCase('pl'))];
  const labelParts = [getOrderProductDisplayDimensions({ ...product, variantAttributes: undefined }) || '', ...variantParts].filter(Boolean);
  return {
    key: identityParts.join('|'),
    label: labelParts.length > 0 ? `${name} · ${labelParts.join(' · ')}` : name,
  };
}

function displayOrderNumber(order: AnalysisOrder): string {
  return order.useManualNumbering && order.manualOrderNumber
    ? order.manualOrderNumber
    : order.orderNumber || '—';
}

/**
 * Split each order across its product lines by their sale-value share. The
 * split always reconciles back to the canonical order revenue/cost/margin, so
 * shared transport and additional costs are not lost or counted twice.
 */
export function buildProductAnalysisOrders(orders: AnalysisOrder[]): ProductAnalysisOrder[] {
  return orders.flatMap((order) => {
    const products = order.products || [];
    if (products.length === 0) return [];
    const weights = products.map((product) => Math.max(
      0,
      Number(product.totalPrice) || (Number(product.saleQuantity) * Number(product.unitPrice)) || 0,
    ));
    const weightTotal = weights.reduce((sum, value) => sum + value, 0);
    const fallbackWeight = products.length > 0 ? 1 / products.length : 0;
    let allocatedRevenue = 0;
    let allocatedCost = 0;
    // An order with an unknown total cost splits into products with an
    // equally unknown cost/margin — never a fabricated per-product zero.
    const costKnown = order.totalCost !== null;

    return products.map((product, index) => {
      const share = weightTotal > 0 ? weights[index] / weightTotal : fallbackWeight;
      const isLast = index === products.length - 1;
      const revenue = isLast ? order.revenue - allocatedRevenue : Number((order.revenue * share).toFixed(2));
      const totalCost = costKnown
        ? (isLast ? (order.totalCost as number) - allocatedCost : Number(((order.totalCost as number) * share).toFixed(2)))
        : null;
      allocatedRevenue += revenue;
      if (totalCost !== null) allocatedCost += totalCost;
      const margin = totalCost === null ? null : Number((revenue - totalCost).toFixed(2));
      const identity = productAnalysisIdentity(product);
      return {
        ...order,
        id: `${order.id}:${product.id}`,
        sourceOrderId: order.id,
        sourceOrderNumber: displayOrderNumber(order),
        productKey: identity.key,
        productLabel: identity.label,
        soldQuantity: Number(product.saleQuantity || product.quantity || 0),
        soldUnit: product.saleUnit || product.unit || 'pcs',
        revenue,
        totalCost,
        margin,
        marginPercent: margin === null ? null : (revenue !== 0 ? (margin / revenue) * 100 : null),
        products: [product],
      };
    });
  });
}

const EMPTY: AnalysisAggregate = {
  orderCount: 0,
  pendingCostOrderCount: 0,
  revenue: 0,
  cost: 0,
  margin: 0,
  marginPercent: 0,
};

/**
 * Adds two possibly-unknown amounts. Once either side is `null` the result
 * stays `null` — an aggregate with one pending-cost order is itself pending,
 * it must never fall back to treating the unknown side as zero.
 */
function nullableAdd(left: number | null, right: number | null): number | null {
  return left === null || right === null ? null : left + right;
}

function finalize(aggregate: AnalysisAggregate): AnalysisAggregate {
  return {
    ...aggregate,
    marginPercent: aggregate.margin === null
      ? null
      : (aggregate.revenue !== 0 ? (aggregate.margin / aggregate.revenue) * 100 : null),
  };
}

/** Cost unknown (`null`) or only partly known (`warehouseCostPending`). */
export function hasIncompleteCost(order: Pick<AnalysisOrder, 'totalCost' | 'warehouseCostPending'>): boolean {
  return order.totalCost === null || order.warehouseCostPending === true;
}

function addOrder(aggregate: AnalysisAggregate, order: AnalysisOrder): AnalysisAggregate {
  return {
    orderCount: aggregate.orderCount + 1,
    pendingCostOrderCount: aggregate.pendingCostOrderCount + (hasIncompleteCost(order) ? 1 : 0),
    revenue: aggregate.revenue + order.revenue,
    cost: nullableAdd(aggregate.cost, order.totalCost),
    margin: nullableAdd(aggregate.margin, order.margin),
    marginPercent: 0,
  };
}

export function aggregateOrders(orders: AnalysisOrder[]): AnalysisAggregate {
  return finalize(orders.reduce(addOrder, { ...EMPTY }));
}

/**
 * Percent is undefined (never a fake "0.0%" or "100%") when there is no
 * revenue to divide by, or when the cost is not fully known — the one rule of
 * orderMarginSummary.getOrderMarginCostState (owner decision 2026-10-05).
 */
export function displayMarginPercent(
  order: Pick<AnalysisOrder, 'revenue' | 'margin' | 'marginPercent'> & Partial<Pick<AnalysisOrder, 'totalCost' | 'warehouseCostPending'>>,
): number | null {
  if (order.margin === null || order.revenue === 0) return null;
  if (order.warehouseCostPending === true
    && getOrderMarginCostState({ totalCost: order.totalCost ?? null, margin: order.margin, warehouseCostPending: true }) !== 'KNOWN') {
    return null;
  }
  return order.marginPercent;
}

/** The order's cost has nothing known yet: its stored margin is the revenue, shown as „koszt nieznany”. */
export function isOrderCostUnknown(order: Pick<AnalysisOrder, 'totalCost' | 'margin' | 'warehouseCostPending'>): boolean {
  return getOrderMarginCostState(order) === 'UNKNOWN';
}

export function getMarginBucket(order: AnalysisOrder): MarginBucket {
  // Owner decision 2026-10-05: an incomplete plan cost (part or all of it
  // unknown) is its own band „Niepełny koszt” — never „Świetna” on a margin
  // that is the revenue without its cost (orderMarginSummary.getOrderMarginCostState).
  if (order.warehouseCostPending === true) return 'incomplete';
  if ((order.totalCost ?? 0) <= 0) return 'none';
  const marginPercent = order.marginPercent ?? 0;
  if (marginPercent < 0) return 'negative';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.low) return 'low';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.medium) return 'medium';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.good) return 'good';
  return 'excellent';
}

/**
 * "Brak kosztów" as the whole page means it: an unknown cost (`null`) and a
 * non-positive cost are both unusable for margin, so both land in the `none`
 * bucket and both render "—" in the margin columns. Counting only the unknown
 * ones is a different, narrower question (the KPI footnote) — keep that rule
 * under its own name instead of widening this one.
 */
export function hasNoCost(order: AnalysisOrder): boolean {
  return (order.totalCost ?? 0) <= 0;
}

export function monthKey(date?: string): string | null {
  return businessDateOf(date)?.slice(0, 7) ?? null;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const absolute = year * 12 + (monthNumber - 1) + delta;
  const shiftedYear = Math.floor(absolute / 12);
  const shiftedMonth = (absolute % 12) + 1;
  return `${shiftedYear}-${String(shiftedMonth).padStart(2, '0')}`;
}

export function getAnalysisGroupIdentity(
  order: AnalysisOrder,
  dimension: AnalysisDimension,
): { key: string; label: string } {
  if (dimension === 'customer') {
    return {
      key: order.customer?.id || order.customer?.name || 'unknown',
      label: order.customer?.name || '—',
    };
  }
  const productOrder = order as ProductAnalysisOrder;
  return {
    key: productOrder.productKey || 'unknown',
    label: productOrder.productLabel || '—',
  };
}

export function matchesAnalysisGroup(
  order: AnalysisOrder,
  dimension: AnalysisDimension,
  groupKey: string,
): boolean {
  return getAnalysisGroupIdentity(order, dimension).key === groupKey;
}

export function aggregateByDimension(
  orders: AnalysisOrder[],
  dimension: AnalysisDimension,
): AnalysisGroup[] {
  const groups = new Map<string, {
    label: string;
    aggregate: AnalysisAggregate;
    monthly: Map<string, AnalysisAggregate>;
    orderIds: Set<string>;
    monthlyOrderIds: Map<string, Set<string>>;
    pendingOrderIds: Set<string>;
    monthlyPendingOrderIds: Map<string, Set<string>>;
  }>();

  orders.forEach((order) => {
    const identity = getAnalysisGroupIdentity(order, dimension);
    const group = groups.get(identity.key) ?? {
      label: identity.label,
      aggregate: { ...EMPTY },
      monthly: new Map<string, AnalysisAggregate>(),
      orderIds: new Set<string>(),
      monthlyOrderIds: new Map<string, Set<string>>(),
      pendingOrderIds: new Set<string>(),
      monthlyPendingOrderIds: new Map<string, Set<string>>(),
    };
    group.aggregate = addOrder(group.aggregate, order);
    const sourceOrderId = (order as ProductAnalysisOrder).sourceOrderId || order.id;
    group.orderIds.add(sourceOrderId);
    // Product rows split one order into many; a pending cost must still be
    // counted once per source order, exactly like `orderIds` above.
    if (hasIncompleteCost(order)) group.pendingOrderIds.add(sourceOrderId);
    const month = monthKey(order.orderDate);
    if (month) {
      group.monthly.set(month, addOrder(group.monthly.get(month) ?? { ...EMPTY }, order));
      const monthOrderIds = group.monthlyOrderIds.get(month) ?? new Set<string>();
      monthOrderIds.add(sourceOrderId);
      group.monthlyOrderIds.set(month, monthOrderIds);
      if (hasIncompleteCost(order)) {
        const monthPendingIds = group.monthlyPendingOrderIds.get(month) ?? new Set<string>();
        monthPendingIds.add(sourceOrderId);
        group.monthlyPendingOrderIds.set(month, monthPendingIds);
      }
    }
    groups.set(identity.key, group);
  });

  // A pending-cost group's magnitude is unknown, not zero, but it still
  // needs *some* weight in the impact chart — treat it as 0 for that ranking
  // input only; the displayed margin/cost themselves stay null (see below).
  const absoluteMargin = Array.from(groups.values())
    .reduce((sum, group) => sum + Math.abs(group.aggregate.margin ?? 0), 0) || 1;

  return Array.from(groups.entries()).map(([key, group]) => ({
    key,
    label: group.label,
    ...finalize({
      ...group.aggregate,
      orderCount: group.orderIds.size,
      pendingCostOrderCount: group.pendingOrderIds.size,
    }),
    impactShare: (Math.abs(group.aggregate.margin ?? 0) / absoluteMargin) * 100,
    monthly: Object.fromEntries(Array.from(group.monthly.entries()).map(([month, aggregate]) => (
      [month, finalize({
        ...aggregate,
        orderCount: group.monthlyOrderIds.get(month)?.size || 0,
        pendingCostOrderCount: group.monthlyPendingOrderIds.get(month)?.size || 0,
      })]
    ))),
  }));
}

export function metricValue(aggregate: AnalysisAggregate, metric: AnalysisMetric): number | null {
  if (metric === 'revenue') return aggregate.revenue;
  if (metric === 'cost') return aggregate.cost;
  if (metric === 'marginPercent') return aggregate.marginPercent;
  return aggregate.margin;
}
